// src/services/enrollmentPaymentAPI.ts
import api from './api';

// ==================== TYPE DEFINITIONS ====================

export interface Week {
  weekNumber: number;
  topic: string;
  price: number;
}

export interface OfferDetails {
  pricePerWeek: number;
  currency: string;
  offerName: string;
  offerActive: boolean;
  validFrom: string;
  validUntil: string;
}

type EnrollmentRequest = {
  selectedWeeks: number[];
  name: string;
  email: string;
  phoneNumber: string;
  tutorialType: "VIRTUAL" | "PHYSICAL" | "HYBRID";
};

export interface EnrollmentResponse {
  id: number;
  name: string;
  email: string;
  phoneNumber: string;
  tutorialType: string;
  selectedWeeks: number[];
  totalAmount: number;
  weeksCount: number;
  enrollmentStatus: string;
  createdAt: string;
  updatedAt: string;
}

export interface PricePreview {
  selectedWeeks: number[];
  weeksCount: number;
  pricePerWeek: number;
  totalAmount: number;
}

export interface PaymentRequest {
  enrollmentId: number;
  phoneNumber: string;
}

export interface PaymentResponse {
  id: number;
  enrollmentId: number;
  transactionReference: string;
  payheroReference?: string;
  amount: number;
  currency: string;
  phoneNumber: string;
  paymentStatus: string;
  mpesaReceiptNumber?: string;
  failureReason?: string;
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
}

export interface PaymentStatusResponse {
  transactionReference: string;
  paymentStatus: 'PENDING' | 'SUCCESS' | 'FAILED';
  mpesaReceiptNumber?: string;
  failureReason?: string;
  amount: number;
  phoneNumber: string;
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
}

// ==================== STATIC DATA (Frontend Only) ====================

/**
 * Week definitions - These are static and don't change
 * No need to fetch from backend
 */
const WEEKS: Week[] = [
  { weekNumber: 1, topic: "Content Creation & Social Media Management", price: 3750 },
  { weekNumber: 2, topic: "Video Editing", price: 3750 },
  { weekNumber: 3, topic: "Meta Ads & Social Media Management", price: 3750 },
  { weekNumber: 4, topic: "Customer Service & Communication", price: 3750 }
];

/**
 * Offer details - Static for current promotion
 * Update these values when offer changes
 */
const OFFER_DETAILS: OfferDetails = {
  pricePerWeek: 3750,
  currency: "KES",
  offerName: "Happy New Year Special",
  offerActive: true,
  validFrom: "2026-01-01",
  validUntil: "2026-02-07"
};

// ==================== API FUNCTIONS ====================

export const enrollmentPaymentAPI = {
  /**
   * Get offer details (static, no API call needed)
   * Returns immediately with hardcoded data
   */
  getOfferDetails: async (): Promise<OfferDetails> => {
    // Simulate slight delay for consistency
    await new Promise(resolve => setTimeout(resolve, 100));
    return OFFER_DETAILS;
  },

  /**
   * Get available weeks (static, no API call needed)
   * Returns immediately with hardcoded data
   */
  getWeeks: async (): Promise<Week[]> => {
    // Simulate slight delay for consistency
    await new Promise(resolve => setTimeout(resolve, 100));
    return WEEKS;
  },

  /**
   * Calculate price preview (client-side calculation)
   * No API call needed - simple multiplication
   */
  calculatePrice: async (selectedWeeks: number[]): Promise<PricePreview> => {
    await new Promise(resolve => setTimeout(resolve, 50));
    
    const weeksCount = selectedWeeks.length;
    const pricePerWeek = OFFER_DETAILS.pricePerWeek;
    const totalAmount = weeksCount * pricePerWeek;

    return {
      selectedWeeks,
      weeksCount,
      pricePerWeek,
      totalAmount
    };
  },

  /**
   * Create enrollment
   * POST /api/enrollments
   */
  createEnrollment: async (data: EnrollmentRequest): Promise<EnrollmentResponse> => {
    try {
      // Normalize and validate data before sending
      const normalizedData = {
        name: data.name.trim(),
        email: data.email.trim().toLowerCase(),
        phoneNumber: normalizePhoneNumber(data.phoneNumber),
        tutorialType: data.tutorialType.toUpperCase(),
        selectedWeeks: data.selectedWeeks
      };

      // Frontend validation
      if (!normalizedData.name || normalizedData.name.length < 2) {
        throw new Error('Name must be at least 2 characters');
      }
      
      if (!normalizedData.email || !normalizedData.email.includes('@')) {
        throw new Error('Please enter a valid email');
      }
      
      if (!validateKenyanPhone(normalizedData.phoneNumber)) {
        throw new Error('Please enter a valid Kenyan phone number (07XX or 01XX)');
      }
      
      if (!normalizedData.selectedWeeks || normalizedData.selectedWeeks.length === 0) {
        throw new Error('Please select at least one week');
      }

      console.log('📤 Sending enrollment data:', normalizedData);

      const response = await api.post('/enrollments', normalizedData);
      return response.data;
    } catch (error: any) {
      console.error('❌ Enrollment creation failed:', error);
      
      // Check if it's a validation error from backend
      if (error.response?.data?.data) {
        const validationErrors = error.response.data.data;
        const errorMessages = Object.values(validationErrors).join('. ');
        throw new Error(errorMessages || 'Validation failed');
      }
      
      throw new Error(error.response?.data?.error || error.message || 'Failed to create enrollment');
    }
  },

  /**
   * Get enrollment by ID
   * GET /api/enrollments/{id}
   */
  getEnrollmentById: async (id: number): Promise<EnrollmentResponse> => {
    try {
      const response = await api.get(`/enrollments/${id}`);
      return response.data;
    } catch (error: any) {
      console.error('❌ Failed to fetch enrollment:', error);
      throw new Error(error.response?.data?.error || 'Enrollment not found');
    }
  },

  /**
   * Initiate payment
   * POST /api/payments
   */
  initiatePayment: async (enrollmentId: number, phoneNumber: string): Promise<PaymentResponse> => {
    try {
      // Normalize phone number to 254XXXXXXXXX format
      const normalizedPhone = normalizePhoneNumber(phoneNumber);
      
      const request: PaymentRequest = {
        enrollmentId,
        phoneNumber: normalizedPhone
      };

      const response = await api.post('/payments', request);
      return response.data;
    } catch (error: any) {
      console.error('❌ Payment initiation failed:', error);
      
      // Handle specific error cases
      if (error.response?.status === 409) {
        throw new Error(error.response.data?.error || 'Payment already exists for this enrollment');
      }
      
      if (error.response?.status === 400) {
        throw new Error(error.response.data?.error || 'Invalid payment request');
      }
      
      throw new Error(error.response?.data?.error || 'Failed to initiate payment');
    }
  },

  /**
   * Check payment status (for polling)
   * GET /api/payments/status/{transactionRef}
   */
  checkPaymentStatus: async (transactionRef: string): Promise<PaymentStatusResponse> => {
    try {
      const response = await api.get(`/payments/status/${transactionRef}`);
      return response.data;
    } catch (error: any) {
      console.error('❌ Payment status check failed:', error);
      
      if (error.response?.status === 404) {
        throw new Error('Payment transaction not found');
      }
      
      throw new Error(error.response?.data?.error || 'Failed to check payment status');
    }
  },

  /**
   * Retry failed payment
   * POST /api/payments/retry
   */
  retryPayment: async (enrollmentId: number): Promise<PaymentResponse> => {
    try {
      const response = await api.post('/payments/retry', { enrollmentId });
      return response.data;
    } catch (error: any) {
      console.error('❌ Payment retry failed:', error);
      throw new Error(error.response?.data?.error || 'Failed to retry payment');
    }
  }
};

// ==================== HELPER FUNCTIONS ====================

/**
 * Normalize phone number to 254XXXXXXXXX format
 * Accepts: 0712345678, 254712345678, +254712345678
 */
function normalizePhoneNumber(phone: string): string {
  // Remove all non-digit characters
  const cleaned = phone.replace(/\D/g, '');
  
  // If starts with 0, replace with 254
  if (cleaned.startsWith('0')) {
    return '254' + cleaned.substring(1);
  }
  
  // If already starts with 254, return as is
  if (cleaned.startsWith('254')) {
    return cleaned;
  }
  
  // Otherwise, assume it's missing country code
  return '254' + cleaned;
}

/**
 * Validate Kenyan phone number
 * Must be Safaricom (07XX) or Airtel (01XX)
 */
export function validateKenyanPhone(phone: string): boolean {
  const normalized = normalizePhoneNumber(phone);
  
  // Must match 254[17]XXXXXXXX (Safaricom 07 or Airtel 01)
  const kenyanPhoneRegex = /^254[17]\d{8}$/;
  return kenyanPhoneRegex.test(normalized);
}

/**
 * Format phone number for display
 * 254712345678 -> +254 712 345 678
 */
export function formatPhoneForDisplay(phone: string): string {
  const normalized = normalizePhoneNumber(phone);
  
  if (normalized.length !== 12) {
    return phone; // Return original if invalid
  }
  
  // Format as +254 7XX XXX XXX
  return `+${normalized.substring(0, 3)} ${normalized.substring(3, 6)} ${normalized.substring(6, 9)} ${normalized.substring(9)}`;
}

// Export for use in components
export default enrollmentPaymentAPI;